// No network or provider dependencies. Root remains the final validator.
export const isReady = (file, now = Date.now()) =>
  file.status === "ready" &&
  Date.parse(file.expiresAt) > now &&
  Date.parse(file.absoluteExpiresAt) > now;
// Root keeps the row after a file is removed or its upload is cancelled: revoked, then cleanup_pending, then deleted.
// None of those is a file the user can still use, so none of them is shown.
export const isGone = (file) =>
  ["revoked", "cleanup_pending", "deleted"].includes(file.status);
export const isUploading = (item) =>
  ["pending", "uploading"].includes(item.status);
export function reconcileFiles(historical, live) {
  const byId = new Map(live.map((file) => [file.id, file]));
  return [
    ...historical.map(
      (file) =>
        byId.get(file.id) || {
          ...file,
          status: "expired",
        },
    ),
    ...live.filter((file) => !historical.some((old) => old.id === file.id)),
  ];
}
export function validateSelection({
  capability,
  files,
  ids,
  model,
  includeVideo,
  now,
}) {
  if (!ids.length) return null;
  if (!capability?.enabled || !capability.turnInputEnabled)
    return {
      // The code gives it errorText's wording in the page language; the message is the fallback.
      code: "ATTACHMENTS_NOT_SUPPORTED",
      message: "附件輸入尚未開放。",
      notices: capability?.notices || [],
    };
  const selected = ids.map((id) => files.find((file) => file.id === id));
  if (selected.some((file) => !file || !isReady(file, now)))
    return {
      code: "ATTACHMENT_EXPIRED",
      message: "原檔已過期，請重新上傳",
      attachmentIds: ids,
    };
  const actual = selected.reduce((sum, file) => sum + file.sizeBytes, 0);
  if (actual > capability.limits.turnBytes)
    return {
      code: "TURN_BYTES_EXCEEDED",
      message: `本輪選取 ${actual.toLocaleString()} bytes，超過 ${capability.limits.turnBytes.toLocaleString()} bytes。`,
      limit: {
        key: "turnBytes",
        value: capability.limits.turnBytes,
        actual,
        unit: "bytes",
      },
      notices: capability.notices,
    };
  const support = capability.models.find((item) => item.id === model);
  if (!support?.supported)
    return {
      code: "MODEL_NOT_SUPPORTED",
      message:
        support?.reason ||
        "此模型尚未開放附件；請自行選擇受支援模型或取消附件。",
      notices: capability.notices,
    };
  if (includeVideo)
    return {
      code: "ATTACHMENT_VIDEO_CONFLICT",
      message: "附件與 video 不能同輪使用；請關閉 video 或取消附件。",
      notices: capability.notices,
    };
  return null;
}
export function toPageMessages(saved) {
  let question = "";
  let files = [];
  let userMessageId;
  return saved.map((message) => {
    if (message.role === "user") {
      question = message.message || "";
      files = message.files || [];
      userMessageId = message.id;
      return {
        id: message.id,
        role: "user",
        text: question,
        files,
      };
    }
    const meta = message.meta || {};
    return {
      ...meta,
      id: message.id,
      role: "assistant",
      text: message.message || "",
      status: "done",
      startedAt: message.timestamp,
      elapsedMs: meta.elapsedMs ?? 0,
      question,
      files,
      userMessageId,
      attachmentTurn: files.length > 0,
    };
  });
}
export function attachmentPatch(delta) {
  const keys = [
    "attachment_statuses",
    "attachment_citations",
    "attachment_db_sources",
    "attachment_faq_sources",
    "attachment_web_sources",
    "attachment_search",
    "attachment_error",
    "attachment_data_gaps",
  ];
  return Object.fromEntries(
    keys
      .filter((key) => Object.prototype.hasOwnProperty.call(delta, key))
      .map((key) => [key, delta[key]]),
  );
}
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes >= 1000000)
    return `${(bytes / 1000000).toFixed(bytes >= 10000000 ? 0 : 1)} MB`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} KB`;
  return `${bytes} B`;
}
export function formatLimit(value, unit) {
  return unit === "pixels"
    ? `${(value / 1000000).toFixed(value >= 10000000 ? 0 : 1)}M 像素`
    : formatBytes(value);
}
export function validateUpload(
  file,
  capability,
  sessionUsageBytes,
  pendingBytes = 0,
  pixels = null,
) {
  const notices = capability.notices;
  if (!capability.acceptedMimeTypes.includes(file.type)) {
    return {
      code: "UNSUPPORTED_MEDIA_TYPE",
      message: `${file.name}：不支援的格式，可接受 PNG、JPEG、WEBP、PDF。`,
      retryable: false,
      notices,
    };
  }
  const key =
    file.type === "application/pdf" ? "pdfFileBytes" : "imageFileBytes";
  const limit = (code, limitKey, actual, unit) => ({
    code,
    retryable: false,
    message: `${file.name}：${{ pdfFileBytes: "檔案太大", imageFileBytes: "檔案太大", sessionBytes: "超過本對話的容量", imagePixels: "圖片解析度太高" }[limitKey]}。`,
    limit: {
      key: limitKey,
      value: capability.limits[limitKey],
      actual,
      unit,
    },
    notices,
  });
  if (file.size > capability.limits[key])
    return limit("FILE_BYTES_EXCEEDED", key, file.size, "bytes");
  const total = sessionUsageBytes + pendingBytes + file.size;
  if (total > capability.limits.sessionBytes)
    return limit("SESSION_BYTES_EXCEEDED", "sessionBytes", total, "bytes");
  if (pixels != null && pixels > capability.limits.imagePixels)
    return limit("IMAGE_PIXELS_EXCEEDED", "imagePixels", pixels, "pixels");
  return null;
}
// Errors where sending the same file again cannot succeed: the file itself has to change.
export const RETRY_USELESS = new Set([
  "UNSUPPORTED_MEDIA_TYPE",
  "FILE_BYTES_EXCEEDED",
  "SESSION_BYTES_EXCEEDED",
  "IMAGE_PIXELS_EXCEEDED",
  "INVALID_FILE",
  "ENCRYPTED_PDF",
  "PDF_SAFETY_LIMIT",
]);
export async function readImagePixels(file) {
  if (typeof createImageBitmap === "function") {
    const image = await createImageBitmap(file);
    try {
      return image.width * image.height;
    } finally {
      image.close();
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image.naturalWidth * image.naturalHeight);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("圖片無法讀取。"));
    };
    image.src = url;
  });
}
